import { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Progress } from "@/components/ui/progress";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Plus, ClipboardList, Pencil } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import ConfirmDelete from "@/components/ConfirmDelete";
import { PeriodFilter, HRMultiFilter, inPeriod, matchesHR, useHROptions } from "@/components/PeriodFilter";

export default function Requisitions() {
  const { branch, user } = useAuth();
  const [items, setItems] = useState([]);
  const [meta, setMeta] = useState({ branches: [], departments: [] });
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({});
  const [preset, setPreset] = useState("all");
  const [range, setRange] = useState({ from: "", to: "" });
  const [hrSel, setHrSel] = useState([]);
  const hrUsers = useHROptions(true);

  const load = async () => {
    const { data } = await api.get("/requisitions");
    setItems(data);
  };

  useEffect(() => {
    load();
    api.get("/meta").then((r) => setMeta(r.data)).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const filtered = items.filter((r) =>
    (branch === "All" || r.branch === branch) &&
    inPeriod(r.requirement_date || r.created_at, range) &&
    matchesHR(r.assigned_hr_id, hrSel)
  );

  const save = async () => {
    try {
      const { id, ...body } = form;
      if (id) {
        await api.patch(`/requisitions/${id}`, body);
        toast.success("Requisition updated");
      } else {
        await api.post("/requisitions", body);
        toast.success("Requisition created");
      }
      setOpen(false);
      setForm({});
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Save failed");
    }
  };

  const canManage = ["super_admin", "hr_manager", "branch_manager"].includes(user?.role);
  const canDelete = ["super_admin", "hr_manager"].includes(user?.role);

  const openEdit = (r) => {
    setForm({
      id: r.id, role_title: r.role_title, positions_required: r.positions_required,
      department: r.department, branch: r.branch,
      target_fill_date: r.target_fill_date || "", status: r.status || "Active",
      assigned_hr: r.assigned_hr || "", assigned_hr_id: r.assigned_hr_id || "",
      timeline_days: r.timeline_days || "", requirement_date: (r.requirement_date || "").slice(0, 10),
      hold_reason: r.hold_reason || "", hold_remarks: r.hold_remarks || "",
    });
    setOpen(true);
  };

  const remove = async (id) => {
    try {
      await api.delete(`/requisitions/${id}`);
      toast.success("Requisition deleted");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Delete failed");
    }
  };

  const statusPill = (s) => {
    const map = {
      Open: "rag-amber", Active: "rag-amber", "In Process": "rag-amber",
      Hold: "bg-slate-200 text-slate-700", Filled: "rag-green", Closed: "rag-green", Cancelled: "rag-red",
    };
    return map[s] || "rag-amber";
  };

  return (
    <div className="space-y-6" data-testid="requisitions-page">
      <div className="flex items-end justify-between">
        <div>
          <div className="label-eyebrow">Hiring Tracker</div>
          <h1 className="font-heading text-3xl font-bold text-[var(--yc-navy)] tracking-tight mt-1">Requisitions &amp; Role Progress</h1>
          <p className="text-slate-600 text-sm mt-1">Live fill rate per role — auto-linked to the candidate pipeline.</p>
        </div>
        {canManage && (
          <Button onClick={() => { setForm({ status: "Active", requirement_date: new Date().toISOString().slice(0, 10) }); setOpen(true); }} className="rounded-full bg-[var(--yc-navy)] hover:bg-[var(--yc-navy-soft)]" data-testid="add-requisition-button">
            <Plus className="h-4 w-4 mr-1" /> New requisition
          </Button>
        )}
      </div>

      <div className="flex gap-3 items-end flex-wrap">
        <PeriodFilter preset={preset} onPreset={setPreset} range={range} onRange={setRange} testId="req-period-filter" />
        <HRMultiFilter value={hrSel} onChange={setHrSel} users={hrUsers} testId="req-hr-filter" />
        {(range.from || hrSel.length > 0) && (
          <button
            onClick={() => { setPreset("all"); setRange({ from: "", to: "" }); setHrSel([]); }}
            className="h-9 px-3 rounded-full border border-slate-200 text-xs text-slate-600 hover:bg-slate-50 transition-colors"
            data-testid="req-reset-filters"
          >
            Reset filters
          </button>
        )}
        <div className="text-xs text-slate-500 pb-2.5" data-testid="requisition-count">
          Showing <span className="font-semibold text-[var(--yc-navy)]">{filtered.length}</span> requisition{filtered.length === 1 ? "" : "s"}
        </div>
      </div>

      <div className="bg-white border border-slate-200 rounded-lg overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-[10px] uppercase tracking-widest text-slate-500 border-b border-slate-100 bg-slate-50">
                <th className="data-cell">Role</th>
                <th className="data-cell">Department · Branch</th>
                <th className="data-cell">Required</th>
                <th className="data-cell">Leads</th>
                <th className="data-cell">Screened</th>
                <th className="data-cell">L1</th>
                <th className="data-cell">L2</th>
                <th className="data-cell">Finalized</th>
                <th className="data-cell">Joined</th>
                <th className="data-cell">Fill %</th>
                <th className="data-cell">Status</th>
                <th className="data-cell">HR · Closure</th>
                <th className="data-cell">Days Open</th>
                <th className="data-cell">Actions</th>
              </tr>
            </thead>
            <tbody>
              {filtered.length === 0 && (
                <tr><td colSpan={14} className="data-cell text-center text-slate-500 py-10">No data found for selected filters.</td></tr>
              )}
              {filtered.map((r) => (
                <tr key={r.id} className="border-b border-slate-100 data-row" data-testid={`requisition-row-${r.id}`}>
                  <td className="data-cell font-medium text-[var(--yc-navy)]">
                    <div className="flex items-center gap-2"><ClipboardList className="h-3.5 w-3.5 text-slate-400" />{r.role_title}</div>
                    <div className="text-xs text-slate-500 mt-0.5">by {r.requested_by}</div>
                  </td>
                  <td className="data-cell text-slate-600">{r.department} · {r.branch}</td>
                  <td className="data-cell font-mono">{r.positions_required}</td>
                  <td className="data-cell font-mono">{r.leads_in}</td>
                  <td className="data-cell font-mono">{r.screening_done}</td>
                  <td className="data-cell font-mono">{r.l1_done}</td>
                  <td className="data-cell font-mono">{r.l2_done}</td>
                  <td className="data-cell font-mono">{r.finalized}</td>
                  <td className="data-cell font-mono">{r.joined}</td>
                  <td className="data-cell w-32">
                    <div className="flex items-center gap-2">
                      <Progress value={r.fill_pct} className="h-1.5" />
                      <span className="text-xs font-mono">{r.fill_pct}%</span>
                    </div>
                  </td>
                  <td className="data-cell">
                    <span className={`px-2 py-0.5 rounded-full text-[10px] uppercase tracking-widest font-semibold ${statusPill(r.status)}`}>
                      {r.status}
                    </span>
                    {r.status === "Hold" && r.hold_reason && (
                      <div className="text-[10px] text-slate-500 mt-1">{r.hold_reason}</div>
                    )}
                    {r.overdue_days > 0 && (
                      <div className="rag-red px-1.5 py-0.5 rounded text-[9px] uppercase tracking-widest font-bold mt-1 inline-block" data-testid={`overdue-badge-${r.id}`}>
                        Overdue {r.overdue_days}d
                      </div>
                    )}
                  </td>
                  <td className="data-cell text-xs text-slate-600">
                    <div>{r.assigned_hr || "Unassigned"}</div>
                    {r.expected_closure_date && <div className="text-slate-400">by {r.expected_closure_date}</div>}
                  </td>
                  <td className="data-cell text-slate-600">{r.days_open}</td>
                  <td className="data-cell">
                    <div className="flex items-center gap-1">
                      {canManage && (
                        <button type="button" onClick={() => openEdit(r)} className="p-1.5 rounded-md text-slate-400 hover:text-[var(--yc-navy)] hover:bg-slate-100 transition-colors" title="Edit" data-testid={`edit-requisition-${r.id}`}>
                          <Pencil className="h-3.5 w-3.5" />
                        </button>
                      )}
                      {canDelete && (
                        <ConfirmDelete onConfirm={() => remove(r.id)} label={`requisition "${r.role_title}"`} testId={`delete-requisition-${r.id}`} />
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle className="font-heading">{form.id ? "Edit requisition" : "New requisition"}</DialogTitle>
            <DialogDescription>Define the role, branch and hiring target.</DialogDescription>
          </DialogHeader>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label className="label-eyebrow">Role title</Label>
              <Input value={form.role_title || ""} onChange={(e) => setForm({ ...form, role_title: e.target.value })} className="mt-1.5" data-testid="req-role-input" />
            </div>
            <div>
              <Label className="label-eyebrow">Positions required</Label>
              <Input type="number" min={1} value={form.positions_required || ""} onChange={(e) => setForm({ ...form, positions_required: Number(e.target.value) })} className="mt-1.5" />
            </div>
            <div>
              <Label className="label-eyebrow">Department</Label>
              <Select value={form.department || ""} onValueChange={(v) => setForm({ ...form, department: v })}>
                <SelectTrigger className="mt-1.5"><SelectValue placeholder="Choose department" /></SelectTrigger>
                <SelectContent>{meta.departments?.map((d) => <SelectItem key={d} value={d}>{d}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label className="label-eyebrow">Branch</Label>
              <Select value={form.branch || ""} onValueChange={(v) => setForm({ ...form, branch: v })}>
                <SelectTrigger className="mt-1.5"><SelectValue placeholder="Choose branch" /></SelectTrigger>
                <SelectContent>{meta.branches?.map((b) => <SelectItem key={b} value={b}>{b}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label className="label-eyebrow">Target fill date</Label>
              <Input type="date" value={(form.target_fill_date || "").slice(0,10)} onChange={(e) => setForm({ ...form, target_fill_date: e.target.value })} className="mt-1.5" />
            </div>
            <div>
              <Label className="label-eyebrow">Status</Label>
              <Select value={form.status || "Active"} onValueChange={(v) => setForm({ ...form, status: v })}>
                <SelectTrigger className="mt-1.5" data-testid="req-status-select"><SelectValue /></SelectTrigger>
                <SelectContent>{["Active", "In Process", "Hold", "Filled", "Closed", "Cancelled"].map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label className="label-eyebrow">Assigned HR</Label>
              <Select value={form.assigned_hr_id || "none"} onValueChange={(v) => {
                const u = hrUsers.find((x) => x.id === v);
                setForm({ ...form, assigned_hr_id: v === "none" ? "" : v, assigned_hr: u?.name || "" });
              }}>
                <SelectTrigger className="mt-1.5" data-testid="req-hr-select"><SelectValue placeholder="—" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">—</SelectItem>
                  {hrUsers.map((u) => <SelectItem key={u.id} value={u.id}>{u.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="label-eyebrow">Requirement date</Label>
              <Input type="date" value={form.requirement_date || ""} onChange={(e) => setForm({ ...form, requirement_date: e.target.value })} className="mt-1.5" data-testid="req-date-input" />
            </div>
            <div>
              <Label className="label-eyebrow">Hiring timeline (days)</Label>
              <Input type="number" min={1} placeholder="e.g., 15" value={form.timeline_days || ""} onChange={(e) => setForm({ ...form, timeline_days: e.target.value ? Number(e.target.value) : "" })} className="mt-1.5" data-testid="req-timeline-input" />
              <div className="text-[10px] text-slate-500 mt-1">Expected closure auto-computes from requirement date + timeline</div>
            </div>
            {form.status === "Hold" && (
              <>
                <div>
                  <Label className="label-eyebrow">Hold reason</Label>
                  <Select value={form.hold_reason || ""} onValueChange={(v) => setForm({ ...form, hold_reason: v })}>
                    <SelectTrigger className="mt-1.5" data-testid="req-hold-reason"><SelectValue placeholder="Why on hold?" /></SelectTrigger>
                    <SelectContent>
                      {["Management Decision Pending", "Budget Issue", "Position Temporarily Stopped", "Requirement Changed", "Candidate Pipeline Issue", "Other"].map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="label-eyebrow">Hold remarks</Label>
                  <Input value={form.hold_remarks || ""} onChange={(e) => setForm({ ...form, hold_remarks: e.target.value })} className="mt-1.5" />
                </div>
              </>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
            <Button onClick={save} className="bg-[var(--yc-navy)] hover:bg-[var(--yc-navy-soft)]" data-testid="save-requisition-button">{form.id ? "Save changes" : "Create"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
